import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";

import CustomBackButton from "@/components/custom/custom-back-button";
import { CustomDisplayItem } from "@/components/custom/custom-display-item";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { useInvoice } from "@/hooks/use-invoice";
import { formatCurrency } from "@/utils/format-currency";
import { formatLabel } from "@/utils/format-label";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  [theme.breakpoints.up("lg")]: { flexDirection: "row" },
}));

const Grid = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(2),
  [theme.breakpoints.up("md")]: { gridTemplateColumns: "1fr 1fr" },
}));

const Side = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  [theme.breakpoints.up("lg")]: { width: 256 },
}));

const SectionTitle = ({ title, description }) => (
  <Side>
    <Typography fontWeight={500} fontSize={18}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    )}
  </Side>
);

const statusStyleMap = {
  SETTLED: { bg: "#ecfdf5", color: "#065f46", dot: "#10b981" },
  UNSETTLED: { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" },
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, "d MMM yyyy") : "N/A";
};

export const InvoiceDetail = () => {
  const { id } = useParams();
  const { data: invoice, isLoading, isError } = useInvoice(id);

  if (isLoading) {
    return (
      <Box sx={{ m: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading invoice details...
        </Typography>
      </Box>
    );
  }

  const status = invoice?.invoiceStatus;
  const statusStyle = statusStyleMap[status] ?? statusStyleMap.UNSETTLED;

  const subtotal = Number(invoice?.sumTotalBeforeVAT || 0);
  const total = Number(invoice?.totalAmount || 0);
  const vat = total - subtotal;

  return (
    <SectionRoot>
      <Stack direction="row" alignItems="center" spacing={2}>
        <CustomBackButton />

        <Typography variant="h6" fontWeight={500}>
          Invoice {invoice?.invoiceNumber ?? id}
        </Typography>

        {invoice && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
              fontSize: "0.78rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: statusStyle.dot,
              }}
            />
            {formatLabel(status ?? "UNKNOWN")}
          </Box>
        )}
      </Stack>

      <Card>
        {isError || !invoice ? (
          <CustomErrorMessage message="Invoice not found." />
        ) : (
          <>
            <Row>
              <SectionTitle
                title="Merchant Information"
                description="The merchant that issued this invoice."
              />
              <Grid>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Merchant"
                    value={invoice.merchantName}
                  />
                </Box>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Merchant ID"
                    value={invoice.merchantId}
                  />
                </Box>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Bank"
                    value={invoice.bankName || "-"}
                  />
                </Box>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Account Number"
                    value={invoice.accountNumber || "-"}
                  />
                </Box>
              </Grid>
            </Row>

            <Divider />

            <Row>
              <SectionTitle
                title="Customer Information"
                description="The buyer this invoice was issued to."
              />
              <Grid>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Customer Name"
                    value={invoice.buyerName}
                  />
                </Box>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Email"
                    value={invoice.buyerEmailAddress || "-"}
                  />
                </Box>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Phone Number"
                    value={invoice.buyerPhoneNumber || "-"}
                  />
                </Box>
                <Box sx={{ backgroundColor: "background.paper" }}>
                  <CustomDisplayItem
                    label="Due Date"
                    value={formatDate(invoice.paymentDueDate)}
                  />
                </Box>
              </Grid>
            </Row>

            <Divider />

            <Row>
              <SectionTitle
                title="Invoice Items"
                description={`Issued ${formatDate(invoice.invoiceDate)}.`}
              />
              <Box sx={{ flex: 1 }}>
                {invoice.itemsList?.map((item, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Qty: {item.quantity} × {formatCurrency(item.unitPrice)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(item.unitPrice * item.quantity)}
                    </Typography>
                  </Stack>
                ))}

                <Box sx={{ mt: 2, ml: "auto", maxWidth: 260 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(subtotal)}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      VAT ({invoice.vatAmount})
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(vat)}
                    </Typography>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Total
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {formatCurrency(total)}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Row>
          </>
        )}
      </Card>
    </SectionRoot>
  );
};
